import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import './ResultViewers.css';

interface TitratorResultViewerProps {
  test: Test;
}

/** Headline results: the statistics means the lab reports. */
const HEADLINE_PARAMS: { key: string; label: string }[] = [
  { key: 'report_export_carbonate', label: 'Carbonate' },
  { key: 'report_export_bicarbonate', label: 'Bicarbonate' },
  { key: 'report_export_li2co3_purity', label: 'Li2CO3 Purity' },
];

const META_PARAMS: { key: string; label: string }[] = [
  { key: 'report_export_method', label: 'Method' },
  { key: 'report_export_replicates', label: 'Replicates' },
  { key: 'report_export_analysis_time', label: 'Analysis Time' },
];

const STAT_NAME_PATTERN = /^report_export_stat_(.+)_name$/;
const REPLICATE_PATTERN = /^report_export_rep(\d+)_(.+)$/;

export const TitratorResultViewer: React.FC<TitratorResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    const rows: [string, string, string][] = [];
    for (const { key, label } of [...HEADLINE_PARAMS, ...META_PARAMS]) {
      const entry = worksheetData[key];
      if (entry == null) continue;
      rows.push([label, formatAnalyticalValue(entry?.value ?? entry), (entry?.unit as string | undefined) ?? '']);
    }
    return rows;
  }, [worksheetData]);

  // Analyte slug → display name, in worksheet order (mirrors the report).
  const analyteNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const key of Object.keys(worksheetData ?? {})) {
      const match = key.match(STAT_NAME_PATTERN);
      if (match) names.set(match[1], worksheetData![key]?.value ?? match[1]);
    }
    return names;
  }, [worksheetData]);

  const statisticsRows = useMemo(() => {
    if (!worksheetData) return [];
    const rows: string[][] = [];
    analyteNames.forEach((name, slug) => {
      const field = (f: string) => worksheetData[`report_export_stat_${slug}_${f}`];
      const mean = field('mean');
      const s = field('s');
      const srel = field('srel');
      rows.push([
        name,
        formatAnalyticalValue(mean?.value),
        mean?.unit ?? '',
        s ? formatAnalyticalValue(s.value) : '',
        srel ? formatAnalyticalValue(srel.value) : '',
        field('n')?.value ?? '',
      ]);
    });
    return rows;
  }, [worksheetData, analyteNames]);

  const replicates = useMemo(() => {
    if (!worksheetData) return { headers: [] as string[], rows: [] as string[][] };
    const byReplicate = new Map<number, Record<string, any>>();
    const slugs: string[] = [];
    for (const key of Object.keys(worksheetData)) {
      const match = key.match(REPLICATE_PATTERN);
      if (!match) continue;
      const [, number, slug] = match;
      if (!byReplicate.has(Number(number))) byReplicate.set(Number(number), {});
      byReplicate.get(Number(number))![slug] = worksheetData[key];
      if (slug !== 'sample_size' && !slugs.includes(slug)) slugs.push(slug);
    }
    const format = (entry: any) =>
      entry == null ? '' : `${formatAnalyticalValue(entry.value)}${entry.unit ? ` ${entry.unit}` : ''}`;
    const entries: Array<[number, Record<string, any>]> = [];
    byReplicate.forEach((values, number) => entries.push([number, values]));
    const rows = entries
      .sort(([a], [b]) => a - b)
      .map(([number, values]) => [
        String(number),
        format(values.sample_size),
        ...slugs.map(slug => format(values[slug])),
      ]);
    return {
      headers: ['Rep', 'Sample Size', ...slugs.map(slug => analyteNames.get(slug) ?? slug)],
      rows,
    };
  }, [worksheetData, analyteNames]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Instrument Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported rv-section-reported-wide">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Parameter', 'Value', 'Unit']}
            rows={reportedRows}
            showRowNumbers={false}
          />

          {statisticsRows.length > 0 && (
            <>
              <h3 className="rv-section-title">Statistics (across replicates)</h3>
              <SpreadsheetTable
                headers={['Analyte', 'Mean', 'Unit', 's', 'srel (%)', 'n']}
                rows={statisticsRows}
                showRowNumbers={false}
              />
            </>
          )}

          {replicates.rows.length > 0 && (
            <>
              <h3 className="rv-section-title">Replicates</h3>
              <SpreadsheetTable headers={replicates.headers} rows={replicates.rows} showRowNumbers={false} />
            </>
          )}
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No titrator results available for this test.</div>
      )}
    </div>
  );
};
